import React, { useEffect, useState } from 'react';
import { LayoutDashboard, SearchX, Package, ShoppingCart, Users, Settings, Layers, Tag, Car, Star, Megaphone, TicketPercent, FileText, Mail, FileSpreadsheet, ShoppingBag, Truck, BarChart3, CreditCard, MessageSquare, MessageCircleQuestion, LineChart, Clock, CircleDot, Camera, Newspaper, ChevronDown, Boxes, Store, PenLine, Gauge, Sparkles, RefreshCw } from 'lucide-react';

export const DASHBOARD = { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard };

export const SECTIONS = [
  { id: 'catalogue', label: 'Catalogue', icon: Boxes, items: [
    { id: 'products', label: 'Produits', icon: Package },
    { id: 'search-misses', label: 'Recherches sans résultat', icon: SearchX, badgeKey: 'search_misses_7d' },
    { id: 'competitors', label: 'Veille tarifaire', icon: LineChart },
    { id: 'cross-sell', label: 'Ventes croisées', icon: Sparkles },
    { id: 'suppliers', label: 'Fournisseurs', icon: Truck },
    { id: 'categories', label: 'Catégories', icon: Layers },
    { id: 'brands', label: 'Marques', icon: Tag },
    { id: 'vehicles', label: 'Véhicules', icon: Car },
    { id: 'plates', label: 'Plaques', icon: CreditCard },
    { id: 'tyre-fitments', label: 'Pneus par véhicule', icon: CircleDot },
    { id: 'assist', label: 'Diagnostics & entretien', icon: Camera },
  ] },
  { id: 'ventes', label: 'Ventes & clients', icon: Store, items: [
    { id: 'orders', label: 'Commandes', icon: ShoppingCart },
    { id: 'shipping', label: 'Livraison', icon: Truck },
    { id: 'customers', label: 'Clients', icon: Users },
    { id: 'sellers', label: 'Vendeurs', icon: Store },
    { id: 'listings', label: 'Annonces d\u2019occasion', icon: Tag },
    { id: 'feeds', label: 'Flux vendeurs', icon: RefreshCw },
    { id: 'mporders', label: 'Commandes occasion', icon: ShoppingBag },
    { id: 'mpshipments', label: 'Expéditions occasion', icon: Truck },
    { id: 'mpcommerce', label: 'Paramètres marketplace', icon: Tag },
    { id: 'cart-reminders', label: 'Paniers abandonnés', icon: ShoppingBag },
    { id: 'reviews', label: 'Avis', icon: Star },
    { id: 'questions', label: 'Questions', icon: MessageCircleQuestion, badgeKey: 'pending_questions' },
  ] },
  { id: 'contenu', label: 'Contenu & marketing', icon: PenLine, items: [
    { id: 'banners', label: 'Bannières', icon: Megaphone },
    { id: 'promos', label: 'Codes promo', icon: TicketPercent },
    { id: 'pages', label: 'Pages', icon: FileText },
    { id: 'articles', label: 'Guides & articles', icon: Newspaper },
    { id: 'emails', label: 'E-mails', icon: Mail },
  ] },
  { id: 'pilotage', label: 'Pilotage', icon: Gauge, items: [
    { id: 'crons', label: 'Alertes & tâches', icon: Clock },
    { id: 'contact', label: 'Messages', icon: MessageSquare },
    { id: 'reports', label: 'Rapports', icon: BarChart3 },
    { id: 'export', label: 'Export comptable', icon: FileSpreadsheet },
    { id: 'settings', label: 'Paramètres', icon: Settings },
  ] },
];

const STORAGE_KEY = 'admin-nav-sections';
const sectionOf = (tab) => SECTIONS.find((s) => s.items.some((i) => i.id === tab))?.id;

const initialOpen = (activeTab) => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (saved && typeof saved === 'object') return saved;
  } catch { /* ignore */ }
  const desktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
  return Object.fromEntries(SECTIONS.map((s) => [s.id, desktop || s.id === sectionOf(activeTab)]));
};

const Badge = ({ value, active, testId }) => (value > 0 ? (
  <span data-testid={testId} className={`text-xs font-bold rounded-full px-2 py-0.5 ${active ? 'bg-white text-orange-600' : 'bg-red-500 text-white'}`}>{value}</span>
) : null);

const NavItem = ({ item, active, badge, onSelect, nested }) => (
  <li>
    <button
      onClick={() => onSelect(item.id)}
      data-testid={`admin-tab-${item.id}`}
      className={`w-full flex items-center gap-3 ${nested ? 'pl-9 pr-3 py-2.5' : 'px-4 py-3'} rounded-lg transition-colors ${active ? 'bg-orange-500 text-white' : 'text-gray-300 hover:bg-[#1f1f23]'}`}
    >
      <item.icon size={nested ? 17 : 20} />
      <span className="flex-1 text-left text-sm">{item.label}</span>
      <Badge value={badge} active={active} testId={`admin-menu-badge-${item.id}`} />
    </button>
  </li>
);

const AdminSidebarNav = ({ activeTab, onSelect, stats }) => {
  const [open, setOpen] = useState(() => initialOpen(activeTab));

  useEffect(() => {
    const sec = sectionOf(activeTab);
    if (sec && !open[sec]) setOpen((o) => ({ ...o, [sec]: true }));
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setOpen((o) => {
    const next = { ...o, [id]: !o[id] };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
  });
  const badgeOf = (item) => (item.badgeKey ? stats?.[item.badgeKey] || 0 : 0);

  return (
    <ul className="space-y-1">
      <NavItem item={DASHBOARD} active={activeTab === DASHBOARD.id} badge={0} onSelect={onSelect} />
      {SECTIONS.map((s) => {
        const isOpen = !!open[s.id];
        const hasActive = s.items.some((i) => i.id === activeTab);
        const total = s.items.reduce((n, i) => n + badgeOf(i), 0);
        return (
          <li key={s.id} data-testid={`admin-nav-section-${s.id}`}>
            <button
              type="button"
              onClick={() => toggle(s.id)}
              aria-expanded={isOpen}
              data-testid={`admin-nav-section-toggle-${s.id}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 mt-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors ${hasActive && !isOpen ? 'text-orange-400' : 'text-gray-400'} hover:text-white hover:bg-[#1f1f23]`}
            >
              <s.icon size={16} />
              <span className="flex-1 text-left">{s.label}</span>
              {!isOpen && <Badge value={total} active={false} testId={`admin-nav-section-badge-${s.id}`} />}
              <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
              <ul className="space-y-0.5 mt-0.5" data-testid={`admin-nav-section-items-${s.id}`}>
                {s.items.map((item) => <NavItem key={item.id} item={item} nested active={activeTab === item.id} badge={badgeOf(item)} onSelect={onSelect} />)}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default AdminSidebarNav;
